import React from 'react';
import { CircleCheck } from 'lucide-react';
import { PrimaryButton } from 'src/shared/components/button/Button';

type CommitGateProps = {
    onCommit: () => void;
    isCommitting: boolean;
    hasCommittableRows: boolean;
    /** Count of rows that will be created on commit (new). */
    newCount: number;
    /** Count of rows that will be updated on commit. */
    updateCount: number;
};

/**
 * Pre-onboard callout. Communicates two things at once:
 *
 *  1. Validation passed (green check, calm) — Ops doesn't have to look for
 *     errors anymore.
 *  2. The next action is theirs ("Click to onboard …") — NOT "done", so we
 *     deliberately avoid `severity='success'` Alert styling that reads as
 *     completed. The button on the right is the only saturated colour in the
 *     callout, drawing the eye to the call to action.
 *
 * Custom layout instead of <Alert> so the icon + text + button align cleanly
 * around a single vertical centre (Alert's `items-start` forces the icon up).
 */
export const CommitGate = ({ onCommit, isCommitting, hasCommittableRows, newCount, updateCount }: CommitGateProps) => {
    const breakdown = hasCommittableRows ? buildBreakdown(newCount, updateCount) : null;

    return (
        <div data-testid='commit-gate' className='flex flex-col gap-2'>
            <div
                data-testid='preview-ready'
                className='rounded-md border border-grey-30 bg-grey-10 p-4 flex items-center justify-between gap-4'
            >
                <div className='flex items-center gap-3'>
                    <CircleCheck className='h-5 w-5 shrink-0 text-green-100' aria-hidden='true' />
                    <div className='flex flex-col'>
                        <span className='font-semibold text-grey-100'>
                            {hasCommittableRows ? 'Validation passed — ready to onboard' : 'Nothing to onboard'}
                        </span>
                        <span className='text-sm text-grey-70'>
                            {hasCommittableRows
                                ? `${breakdown} · Click ${isCommitting ? 'Onboarding…' : 'Onboard sites'} to write the changes.`
                                : 'Every row was classified as unchanged.'}
                        </span>
                    </div>
                </div>
                <PrimaryButton onClick={onCommit} disabled={isCommitting || !hasCommittableRows} data-testid='commit-button'>
                    {isCommitting ? 'Onboarding…' : 'Onboard sites'}
                </PrimaryButton>
            </div>
        </div>
    );
};

const buildBreakdown = (newCount: number, updateCount: number): string => {
    const parts: string[] = [];
    if (newCount > 0) parts.push(`${newCount} to create`);
    if (updateCount > 0) parts.push(`${updateCount} to update`);
    return parts.join(' + ');
};
